import React, {useEffect, useRef, useState} from 'react';
import {Button, Card, Form, Input, Modal, Space, Upload} from 'antd';
import {UploadOutlined} from '@ant-design/icons';
import useHTTP from '@/hooks/useHTTP.mjs';
import runAsync from '@/script/runAsync.mjs';
import {useOpenModal} from '@/components/Modal/index.mjs';
import SelectModuleDialog from '@/pages/MainPage/dialogs/SelectModuleDialog';
import SelectFcDialog from '@/pages/MainPage/dialogs/SelectFcDialog.jsx';
import Table from '@/biz/Table.jsx';
import BizNodeTypeTag, {convertToBizNodeTypeName} from "@/components/BizNodeTypeTag/BizNodeTypeTag";

/**
 * AI迭代功能方案
 * @param param
 *  modules: [{moduleType, moduleId, moduleName, reqDesc, files}]
 * @param onOk
 * @param props
 * @returns {JSX.Element}
 * @constructor
 */
const Pane = ({
                  param = {
                      prjId,
                      sVer,
                      fmIds: [],
                      modules: [],
                      reqDesc,
                      files: []
                  }, onOk, ...props
              }) => {
    const http = useHTTP();
    const openModal = useOpenModal();

    // 表单
    const [form] = Form.useForm();

    // 数据
    const [prj, setPrj] = useState({});
    const [rows, setRows] = useState([]);
    const [selectedRows, setSelectedRows] = useState([]);
    const [fileList, setFileList] = useState([]);

    // 加载显示
    const [loading, setLoading] = useState(false);

    const ref = useRef({});

    // 组件初始化
    useEffect(() => {
        (async () => {
            init()
        })()
    }, []);

    const init = async () => {
        try {
            setLoading(true)
            ref.current.fileList = [];
            form.resetFields();

            doQueryPrj({prjId: param.prjId})

            if (param.files && param.files.length > 0) {
                setFileList([...param.files])
                ref.current.fileList = [...param.files];
            }
            form.setFieldsValue({reqDesc: param.reqDesc});

            const modules = param.modules;
            const fmIds = param.fmIds;
            if (modules && modules.length > 0) {
                for (var item of modules) {
                    item.pkid = item.moduleId;
                    item.reqTitle = '修改' + (item.moduleName ?? '');
                    item.reqType = 'UPT';
                }
            } else if (fmIds && fmIds.length > 0){
                const result = await doQueryBdsAndUis(param);
                const bds = result.dmBds || [];
                const uis = result.umUis || [];

                if (bds && bds.length > 0) {
                    for (var item of bds) {
                        item.moduleType = "BD";
                        item.moduleName = item.bdName;
                        item.moduleId = item.pkid;
                        item.moduleCode = item.bdCode;

                        item.pkid = item.pkid;
                        item.reqTitle = '修改' + (item.bdName ?? '');
                        item.reqType = 'UPT';

                        modules.push(item);
                    }
                }

                if (uis && uis.length > 0) {
                    for (var item of uis) {
                        item.moduleType = "UI";
                        item.moduleName = item.uiName;
                        item.moduleId = item.pkid;
                        item.moduleCode = item.uiCode;

                        item.pkid = item.pkid;
                        item.reqTitle = '修改' + (item.uiName ?? '');
                        item.reqType = 'UPT';

                        modules.push(item);
                    }
                }
            }

            setRows([...modules])
        } finally {
            setLoading(false)
        }
    }

    const doQueryPrj = async ({prjId}) => {
        const data = await runAsync(
            async () => {
                return await http.get(`/prjs/${prjId}`);
            }, {
                error: {content: (err) => `查询项目失败: ${err.message}`},
                loading: {show: false},
                success: {show: false},
            });

        setPrj({...data});
    }

    const doQueryBdsAndUis = async ({prjId, sVer, fmIds}) => {
        const result = await http.get(
            '/archfms/listDmBdAndUmUiWithRefByFmIds',
            {prjId, sVer, fmIds}
        )
        return result;
    }

    // 确认
    const doConfirm = async () => {
        try {
            setLoading(true);
            const formField = await form.validateFields();

            let result = {
                bdIds: rows.filter(v => v.moduleType == 'BD').map(item => item.pkid),
                uiIds: rows.filter(v => v.moduleType == 'UI').map(item => item.pkid),
                dtIds: rows.filter(v => v.moduleType == 'DT').map(item => item.pkid),
                reqDesc: formField.reqDesc,
                files: ref.current.fileList.map(file => ({fileId: file.fileId, name: file.name, size: file.size})),
            };

            onOk(result);
        } catch (err) {
            console.log(err);
        } finally {
            setLoading(false);
        }
    }

    const columns = [
        {
            title: '模件类型',
            dataIndex: 'moduleType',
            width: 100,
            ellipsis: true,
            getValue: record => {
                return convertToBizNodeTypeName(record.moduleType, null, prj.subTypeCode)
            },
            filter: <Table.FilterText/>,
            render: (text, record) => {
                return <BizNodeTypeTag bizNodeType={record.moduleType} subTypeCode={prj.subTypeCode}/>
            }
        },
        {
            title: '模件名称',
            dataIndex: 'moduleName',
            width: 150,
            ellipsis: true,
            filter: <Table.FilterText/>,
        }
    ];

    const cardNoBottomCss = {
        '& .ant-card-body': {
            paddingBottom: '0px',
        },
    }

    const cardSmallTopCss = {
        '& .ant-card-body': {
            paddingTop: '12px',
            paddingBottom: '12px',
        },
    }

    // 选择迭代版模件
    const doAddIterative = async () => {
        let result = await openModal(<SelectModuleDialog isSingle={false} prjId={param.prjId} sVer={param.sVer}
                                                         filterBizNodeTypes={['BD', 'UI', 'DT']}/>)
        if (result && result.length > 0) {
            result.forEach(item => {
                if (!rows.find(elem => elem.pkid == item.pkid)) {
                    const module = {
                        pkid: item.pkid,
                        moduleType: item.dpBizNodeType,
                        moduleId: item.pkid,
                        moduleName: item.dpName
                    }
                    rows.push(module);
                }

            })
        }
        setRows([...rows]);
    }

    const doRemove = () => {
        const filterArrays = selectedRows.map(({pkid}) => pkid)
        setRows(rows.filter(({pkid}) => !filterArrays.includes(pkid)))
        setSelectedRows([])
    }

    const rowSelection = {
        selectedRowKeys: selectedRows.map(({pkid}) => pkid),
        onChange: (selectedRowKeys, selectedRows) => {
            setSelectedRows(selectedRows);
        },
    }

    const uploadFile = async (data) => {
        try {
            const {fileId} = await runAsync(
                async () => {
                    return await http.upload({'pathName': param.prjId, 'file': data.file});
                }, {
                    error: {content: (err) => `上传失败: ${err.message}`},
                    loading: {show: true},
                    success: {show: false},
                });
            if (fileId) {
                data.file.fileId = fileId;
                ref.current.fileList = ([...ref.current.fileList, data.file]);
                setFileList([...ref.current.fileList])
            }
        } catch (err) {
            console.log(err);
        }
    }

    return (
        <>
            <Modal
                title={'AI迭代功能方案'}
                okText="确定"
                cancelText="取消"
                width={1500}
                bodyStyle={{maxHeight: '70vh', overflowY: 'auto'}}
                {...props}
                footer={[
                    <Space>
                        <Button onClick={() => onOk()}>取消</Button>
                        <Button type="primary" onClick={() => doConfirm()} loading={loading}>确定</Button>
                    </Space>
                ]}
            >
                <Form form={form} layout="vertical">
                    <Card title={"迭代范围"} css={cardSmallTopCss}>
                        <div style={{display: 'flex', justifyContent: 'space-between', marginBottom: 12,}}>
                            <Space>
                                <Button type="primary" size={'small'} onClick={() => doAddIterative()}>新增</Button>
                                <Button type="danger" size={'small'} onClick={() => doRemove()}>删除</Button>
                            </Space>
                        </div>
                        {rows?.length > 0 && <Table
                            size="middle"
                            columns={columns}
                            dataSource={rows}
                            noRowNumber={true}
                            rowKey={"pkid"}
                            rowSelection={{type: "checkbox", ...rowSelection}}
                        />}
                    </Card>

                    <Card title={"需求描述"} style={{marginTop: '20px'}} css={cardSmallTopCss}>
                        <Form.Item
                            name={'reqDesc'}
                            label=""
                        >
                            <Input.TextArea size="small" rows={5}/>
                        </Form.Item>
                        <div style={{display: 'flex', justifyContent: 'space-between'}}>
                            <Upload
                                fileList={fileList}
                                multiple={true}
                                showUploadList={true}
                                customRequest={uploadFile}
                                beforeUpload={(file, files) => {
                                    setFileList([...fileList, ...files]);
                                    return true;
                                }}
                                onRemove={(file) => {
                                    if (file) {
                                        const notDeletes = fileList.filter(f => f.fileId != file.fileId);
                                        setFileList([...notDeletes]);
                                    }
                                }}
                            >
                                <div className="ant-upload-btn bg-grey-light">
                                    <Button type="primary" size={'small'} icon={<UploadOutlined/>}>需求附件上传</Button>
                                </div>
                            </Upload>
                        </div>
                    </Card>
                </Form>
            </Modal>
        </>
    );
}

export default Pane;
